import { AppStoreBadge } from "@/site/components/app-store-badge";
import { FOOTER_PATTERN } from "@/lib/patterns";
import { Link } from "@tanstack/react-router";

const APP_STORE_URL =
  "https://apps.apple.com/app/birthdayalarm/id594352154?pt=405762&mt=8&ct=bday-web-footer";
const FACEBOOK_URL = "https://www.facebook.com/BirthdayChannel";
const INSTAGRAM_URL = "https://www.instagram.com/birthdayalarm";
const X_URL = "https://x.com/birthdayalarm";

const linkClass = "px-2.5 text-inherit no-underline hover:text-inherit";

// Verbatim ports of react-icons/bs BsFacebook, BsInstagram, BsTwitterX (X logo) —
// same paths the old app rendered (Twitter → X).
function FacebookIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 16 16"
      aria-hidden
      {...props}
    >
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
    </svg>
  );
}

function InstagramIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 16 16"
      aria-hidden
      {...props}
    >
      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
    </svg>
  );
}

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 16 16"
      aria-hidden
      {...props}
    >
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
    </svg>
  );
}

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="z-[100] mt-auto flex flex-col items-center justify-between border-t border-tan px-[10px] py-[15px] text-[14px] text-foreground shadow-[inset_0_2px_10px_rgba(0,0,0,0.1)] min-[992px]:flex-row min-[992px]:p-[15px] min-[992px]:text-base min-[992px]:leading-[20.8px]"
      style={{
        backgroundImage: `url("${FOOTER_PATTERN}")`,
        backgroundRepeat: "repeat",
        fontWeight: 500,
      }}
      data-testid="site-footer"
    >
      <div className="text-center min-[992px]:text-left">
        <div className="mb-4">
          <Link to="/about" className={linkClass} data-testid="link-footer-about">
            About Us
          </Link>
          <span aria-hidden>|</span>
          <Link to="/faq" className={linkClass} data-testid="link-footer-faq">
            FAQ
          </Link>
          <span aria-hidden>|</span>
          <Link to="/contact" className={linkClass} data-testid="link-footer-contact">
            Contact Us
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-center leading-[28px] min-[992px]:justify-start min-[992px]:leading-[20.8px]">
          <Link
            to="/company"
            className={`${linkClass} text-[14px] text-grey-dark`}
            data-testid="link-footer-copyright"
          >
            <span className="whitespace-nowrap">&copy; 2001-{year}</span>
          </Link>
          <span aria-hidden>&bull;</span>
          <Link
            to="/company"
            className={`${linkClass} text-[14px] text-grey-dark`}
            data-testid="link-footer-legal"
          >
            Legal
          </Link>
          <span aria-hidden>&bull;</span>
          <Link
            to="/terms"
            className={`${linkClass} text-[14px] text-grey-dark`}
            data-testid="link-footer-terms"
          >
            Terms
          </Link>
          <span aria-hidden>&bull;</span>
          <Link
            to="/privacy"
            className={`${linkClass} text-[14px] text-grey-dark`}
            data-testid="link-footer-privacy"
          >
            Privacy Policy
          </Link>
          <span aria-hidden>&bull;</span>
          <Link
            to="/safety"
            className={`${linkClass} text-[14px] text-grey-dark`}
            data-testid="link-footer-safety"
          >
            Safety
          </Link>
        </div>
      </div>

      <div className="order-first my-4 flex flex-1 flex-wrap items-center justify-center gap-8 min-[992px]:order-none min-[992px]:my-0 min-[992px]:flex-none">
        <a
          href={FACEBOOK_URL}
          target="_blank"
          rel="noreferrer"
          className="text-inherit no-underline"
          data-testid="link-footer-facebook"
        >
          <FacebookIcon className="h-[1.8rem] w-[1.8rem]" />
          <span className="sr-only">Facebook</span>
        </a>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noreferrer"
          className="text-inherit no-underline"
          data-testid="link-footer-instagram"
        >
          <InstagramIcon className="h-[1.8rem] w-[1.8rem]" />
          <span className="sr-only">Instagram</span>
        </a>
        <a
          href={X_URL}
          target="_blank"
          rel="noreferrer"
          className="text-inherit no-underline"
          data-testid="link-footer-x"
        >
          <XIcon className="h-[1.8rem] w-[1.8rem]" />
          <span className="sr-only">X</span>
        </a>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Download BirthdayAlarm on the App Store"
          className="block"
        >
          <AppStoreBadge className="block w-40" />
        </a>
      </div>
    </footer>
  );
}

export function SimpleFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="mt-auto flex min-h-[70px] items-center justify-center border-t border-tan px-4 text-[16px] font-medium text-foreground shadow-[inset_0_2px_10px_rgba(0,0,0,0.1)] max-md:items-start max-md:py-[15px] max-md:text-[14px]"
      style={{ backgroundImage: `url("${FOOTER_PATTERN}")`, backgroundRepeat: "repeat" }}
      data-testid="site-footer-simple"
    >
      {/* mt-[10px] mirrors the old inner div's `margin: 10px auto 0`; below md the old
          footer switches to 14px, column flow (text at top), and 15px vertical padding. */}
      <span className="mt-[10px]">&copy; 2001-{year} birthdayalarm.com</span>
    </footer>
  );
}
